/**
 * Content API Client
 * Handles all communication with the Content Service (port 10023)
 * Routes are workspace-scoped under /api/workspaces/:workspaceId/content
 */

const DEFAULT_TIMEOUT = 30000;
const MAX_RETRIES = 2;
const BASE_RETRY_DELAY = 1000;

class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

async function fetchWithTimeout(
  url: string,
  options: RequestInit,
  timeout: number = DEFAULT_TIMEOUT
): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } finally {
    clearTimeout(timeoutId);
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

async function fetchWithRetry(
  url: string,
  options: RequestInit,
  retries: number = MAX_RETRIES
): Promise<Response> {
  let lastError: Error | null = null;
  for (let attempt = 0; attempt < retries; attempt++) {
    try {
      return await fetchWithTimeout(url, options);
    } catch (error) {
      lastError = error as Error;
      if (attempt < retries - 1) {
        await sleep(BASE_RETRY_DELAY * Math.pow(2, attempt));
      }
    }
  }
  throw lastError || new Error('Request failed after retries');
}

async function request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const response = await fetchWithRetry(endpoint, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    credentials: 'include',
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new ApiError(response.status, errorText || response.statusText);
  }

  if (response.status === 204) return null as T;
  return response.json();
}

function baseUrl(workspaceId: string) {
  return `/api/workspaces/${workspaceId}/content`;
}

// =============================================================================
// Types (mirrors backend types for frontend use)
// =============================================================================

export type ContentStatus =
  | 'planned' | 'drafting' | 'design_ready' | 'in_review'
  | 'final_edits' | 'ready_to_publish' | 'published' | 'archived';

export type ContentType =
  | 'article' | 'guide' | 'playbook' | 'infographic' | 'faq'
  | 'news' | 'case_study' | 'whitepaper' | 'video'
  | 'linkedin_post' | 'social_post' | 'thread' | 'proposal'
  | 'presentation' | 'one_pager' | 'battle_card' | 'email_sequence'
  | 'newsletter' | 'press_release' | 'webinar' | 'slides' | 'podcast'
  | 'policy' | 'procedure'
  // CMS website & KB types
  | 'blog_post' | 'landing_page' | 'website_page'
  | 'kb_article' | 'kb_collection' | 'site_settings' | 'navigation';

export interface ContentItem {
  id: string;
  project_id: string;
  workspace_id: string;
  document_id: string | null;
  title: string;
  slug: string | null;
  content_type: ContentType;
  campaign: string | null;
  pillar: string | null;
  status: ContentStatus;
  primary_author_id: string | null;
  meta_description: string | null;
  seo_keywords: string[];
  hero_image_url: string | null;
  hero_image_alt: string | null;
  planned_publish_date: string | null;
  actual_publish_date: string | null;
  published_url: string | null;
  is_featured: boolean;
  tags: string[];
  notes: string | null;
  checklist: unknown[];
  template_id: string | null;
  created_at: string;
  updated_at: string;
  author_name?: string;
}

export interface Author {
  id: string;
  workspace_id: string;
  name: string;
  email: string | null;
  avatar_url: string | null;
  role: string | null;
  bio: string | null;
  tone_instructions: string | null;
  writing_samples: unknown[];
  linkedin_profile_url: string | null;
  linkedin_urn: string | null;
  created_at: string;
  updated_at: string;
}

export interface SocialCopy {
  id: string;
  content_item_id: string;
  author_id: string;
  platform: string;
  copy_text: string | null;
  distributed_at: string | null;
  distribution_url: string | null;
  created_at: string;
  updated_at: string;
  author_name?: string;
}

export interface ImportResult {
  total: number;
  created: number;
  skipped: number;
  errors: string[];
  documents_created?: number;
  documents_failed?: number;
}

export type ScheduleStatus = 'pending' | 'executing' | 'completed' | 'failed' | 'cancelled';
export type ScheduleType = 'publish' | 'social_distribute' | 'reminder';

export interface ContentSchedule {
  id: string;
  content_item_id: string;
  schedule_type: ScheduleType;
  scheduled_at: string;
  executed_at: string | null;
  status: ScheduleStatus;
  target_author_ids: string[];
  target_platforms: string[];
  error_message: string | null;
  created_at: string;
  updated_at: string;
  // Joined fields from distribution/queue endpoint
  content_title?: string;
}

export interface ContentComment {
  id: string;
  content_item_id: string;
  platform: string;
  external_comment_id: string | null;
  author_name: string | null;
  author_profile_url: string | null;
  comment_text: string;
  reply_text: string | null;
  replied_at: string | null;
  commented_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface ItemAnalyticsDataPoint {
  day: string;
  metric_type: string;
  total: number;
}

export interface ChannelCredential {
  id: string;
  platform: string;
  credential_type: string;
  is_valid: boolean;
  last_validated_at: string | null;
  expires_at: string | null;
  created_at: string;
  updated_at: string;
}

// CMS Media Asset
export interface MediaAsset {
  id: string;
  workspace_id: string;
  file_id: string;
  filename: string;
  mime_type: string;
  file_size: number | null;
  url: string;
  alt_text: string;
  caption: string;
  tags: string[];
  created_at: string;
  updated_at: string;
}

// CMS Version History
export interface ContentVersion {
  id: string;
  content_item_id: string;
  version_number: number;
  snapshot?: ContentItem;
  change_summary: string | null;
  created_by: string | null;
  created_at: string;
}

export interface ContentItemFilters {
  status?: ContentStatus;
  content_type?: ContentType;
  campaign?: string;
  pillar?: string;
  primary_author_id?: string;
  tags?: string;
  is_featured?: string;
  date_from?: string;
  date_to?: string;
  search?: string;
}

// AI Generation types
export interface GenerateSocialCopyRequest {
  content_item_id: string;
  author_id: string;
  platform: string;
  custom_instruction?: string;
  project_id?: string;
}

export interface GenerateSocialCopyResponse {
  generated_text: string;
  model: string;
  tokens_used: number;
}

export interface BatchGenerateRequest {
  content_item_id: string;
  author_ids: string[];
  platforms: string[];
  custom_instruction?: string;
  project_id?: string;
}

export interface BatchGenerateResponse {
  results: { author_id: string; platform: string; generated_text: string }[];
  total_tokens: number;
}

// Style Analysis types
export interface StyleProfile {
  tone: string;
  structure_patterns: string[];
  hooks: string[];
  vocabulary: string[];
  formatting: string[];
  persona_summary: string;
  sample_count: number;
}

export interface EssaySection {
  title: string;
  source_excerpt: string;
  angle: string;
  hook_suggestion: string;
  key_quotes: string[];
  discussion_question: string;
}

export interface SeriesResult {
  content_items: Array<{ id: string; title: string; section_angle: string }>;
  social_copies: Array<{ content_item_id: string; copy_text: string }>;
  style_profile?: StyleProfile;
  sections: EssaySection[];
}

// Brand Voice types
export interface BrandVoice {
  id: string;
  workspace_id: string;
  guidelines: string | null;
  tone_keywords: string[];
  avoid_keywords: string[];
  examples: unknown[];
  channel_rules: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

// =============================================================================
// Status helpers
// =============================================================================

export const STATUS_LABELS: Record<ContentStatus, string> = {
  planned: 'Planned',
  drafting: 'Drafting',
  design_ready: 'Design Ready',
  in_review: 'In Review',
  final_edits: 'Final Edits',
  ready_to_publish: 'Ready to Publish',
  published: 'Published',
  archived: 'Archived',
};

export const STATUS_COLORS: Record<ContentStatus, string> = {
  planned: 'bg-neutral-100 text-neutral-600',
  drafting: 'bg-amber-100 text-amber-700',
  design_ready: 'bg-violet-100 text-violet-700',
  in_review: 'bg-blue-100 text-blue-700',
  final_edits: 'bg-orange-100 text-orange-700',
  ready_to_publish: 'bg-emerald-100 text-emerald-700',
  published: 'bg-green-100 text-green-700',
  archived: 'bg-neutral-200 text-neutral-500',
};

export const CONTENT_TYPE_LABELS: Record<ContentType, string> = {
  article: 'Article',
  guide: 'Guide',
  playbook: 'Playbook',
  infographic: 'Infographic',
  faq: 'FAQ',
  news: 'News',
  case_study: 'Case Study',
  whitepaper: 'Whitepaper',
  video: 'Video',
  linkedin_post: 'LinkedIn Post',
  social_post: 'Social Post',
  thread: 'Thread',
  proposal: 'Proposal',
  presentation: 'Presentation',
  one_pager: 'One-Pager',
  battle_card: 'Battle Card',
  email_sequence: 'Email Sequence',
  newsletter: 'Newsletter',
  press_release: 'Press Release',
  webinar: 'Webinar',
  slides: 'Slides',
  podcast: 'Podcast',
  policy: 'Policy',
  procedure: 'Procedure',
  // CMS types
  blog_post: 'Blog Post',
  landing_page: 'Landing Page',
  website_page: 'Website Page',
  kb_article: 'KB Article',
  kb_collection: 'KB Collection',
  site_settings: 'Site Settings',
  navigation: 'Navigation',
};

export const ALL_CONTENT_TYPES: ContentType[] = [
  'article', 'guide', 'playbook', 'infographic', 'faq',
  'news', 'case_study', 'whitepaper', 'video',
  'linkedin_post', 'social_post', 'thread', 'proposal',
  'presentation', 'one_pager', 'battle_card', 'email_sequence',
  'newsletter', 'press_release', 'webinar', 'slides', 'podcast',
  'policy', 'procedure',
];

export const ALL_STATUSES: ContentStatus[] = [
  'planned', 'drafting', 'design_ready', 'in_review',
  'final_edits', 'ready_to_publish', 'published', 'archived',
];

export const SCHEDULE_STATUS_LABELS: Record<ScheduleStatus, string> = {
  pending: 'Pending',
  executing: 'Executing',
  completed: 'Completed',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

export const SCHEDULE_STATUS_COLORS: Record<ScheduleStatus, string> = {
  pending: 'bg-amber-100 text-amber-700',
  executing: 'bg-blue-100 text-blue-700',
  completed: 'bg-green-100 text-green-700',
  failed: 'bg-red-100 text-red-700',
  cancelled: 'bg-neutral-100 text-neutral-500',
};

export const SCHEDULE_TYPE_LABELS: Record<ScheduleType, string> = {
  publish: 'Publish Article',
  social_distribute: 'Social Distribution',
  reminder: 'Reminder',
};

// Policy visibility helpers
export const POLICY_CONTENT_TYPES: ContentType[] = ['policy', 'procedure'];

export const VISIBILITY_LABELS: Record<string, string> = {
  internal: 'Internal',
  public: 'Public-Facing',
};

export const VISIBILITY_COLORS: Record<string, string> = {
  internal: 'bg-amber-100 text-amber-700',
  public: 'bg-emerald-100 text-emerald-700',
};

// =============================================================================
// API Methods
// =============================================================================

export const contentApi = {
  // Content Items
  async listItems(workspaceId: string, filters?: ContentItemFilters): Promise<ContentItem[]> {
    const params = new URLSearchParams();
    if (filters) {
      for (const [key, value] of Object.entries(filters)) {
        if (value !== undefined && value !== '') params.set(key, String(value));
      }
    }
    const qs = params.toString() ? `?${params.toString()}` : '';
    const data = await request<{ items: ContentItem[] }>(`${baseUrl(workspaceId)}/items${qs}`);
    return data.items;
  },

  async getItem(workspaceId: string, itemId: string): Promise<ContentItem> {
    return request<ContentItem>(`${baseUrl(workspaceId)}/items/${itemId}`);
  },

  async createItem(workspaceId: string, data: Partial<ContentItem> & { project_id?: string }): Promise<ContentItem> {
    return request<ContentItem>(`${baseUrl(workspaceId)}/items`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async updateItem(workspaceId: string, itemId: string, data: Partial<ContentItem>): Promise<ContentItem> {
    return request<ContentItem>(`${baseUrl(workspaceId)}/items/${itemId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  async deleteItem(workspaceId: string, itemId: string): Promise<void> {
    await request<void>(`${baseUrl(workspaceId)}/items/${itemId}`, { method: 'DELETE' });
  },

  async publishToSanity(
    workspaceId: string,
    itemId: string,
    data: { title: string; slug: string; description: unknown[]; sections: unknown[]; seo: { metaTitle: string; metaDescription: string; canonicalUrl: string } },
  ): Promise<{ success: boolean; sanity_doc_id: string; sanity_draft_id: string; sanity_studio_url: string }> {
    return request(`${baseUrl(workspaceId)}/items/${itemId}/publish-sanity`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // Authors
  async listAuthors(workspaceId: string): Promise<Author[]> {
    const data = await request<{ authors: Author[] }>(`${baseUrl(workspaceId)}/authors`);
    return data.authors;
  },

  async getAuthor(workspaceId: string, authorId: string): Promise<Author> {
    return request<Author>(`${baseUrl(workspaceId)}/authors/${authorId}`);
  },

  async createAuthor(workspaceId: string, data: Partial<Author>): Promise<Author> {
    return request<Author>(`${baseUrl(workspaceId)}/authors`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async updateAuthor(workspaceId: string, authorId: string, data: Partial<Author>): Promise<Author> {
    return request<Author>(`${baseUrl(workspaceId)}/authors/${authorId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  async deleteAuthor(workspaceId: string, authorId: string): Promise<void> {
    await request<void>(`${baseUrl(workspaceId)}/authors/${authorId}`, { method: 'DELETE' });
  },

  // Social Copies
  async getSocialCopies(workspaceId: string, itemId: string): Promise<SocialCopy[]> {
    const data = await request<{ social_copies: SocialCopy[] }>(
      `${baseUrl(workspaceId)}/items/${itemId}/social-copies`
    );
    return data.social_copies;
  },

  async upsertSocialCopy(
    workspaceId: string,
    itemId: string,
    authorId: string,
    platform: string,
    copyText: string
  ): Promise<SocialCopy> {
    return request<SocialCopy>(
      `${baseUrl(workspaceId)}/items/${itemId}/social-copies/${authorId}/${platform}`,
      { method: 'PUT', body: JSON.stringify({ copy_text: copyText }) }
    );
  },

  // Import
  async importCsv(workspaceId: string, csvContent: string, projectId?: string): Promise<ImportResult> {
    return request<ImportResult>(`${baseUrl(workspaceId)}/import/csv`, {
      method: 'POST',
      body: JSON.stringify({ csv_content: csvContent, project_id: projectId }),
    });
  },

  async importNotion(workspaceId: string, directory?: string, projectId?: string): Promise<ImportResult> {
    return request<ImportResult>(`${baseUrl(workspaceId)}/import/notion`, {
      method: 'POST',
      body: JSON.stringify({ directory, project_id: projectId }),
    });
  },

  // Calendar
  async getCalendarItems(workspaceId: string, from: string, to: string): Promise<ContentItem[]> {
    return this.listItems(workspaceId, { date_from: from, date_to: to });
  },

  // Analytics
  async getAnalyticsSummary(workspaceId: string, days?: number): Promise<Record<string, number>> {
    const qs = days ? `?days=${days}` : '';
    return request<Record<string, number>>(`${baseUrl(workspaceId)}/analytics/summary${qs}`);
  },

  // Distribution
  async getDistributionQueue(workspaceId: string): Promise<ContentSchedule[]> {
    const data = await request<{ queue: ContentSchedule[] }>(`${baseUrl(workspaceId)}/distribution/queue`);
    return data.queue;
  },

  async listSchedules(workspaceId: string, status?: ScheduleStatus): Promise<ContentSchedule[]> {
    const qs = status ? `?status=${status}` : '';
    const data = await request<{ schedules: ContentSchedule[] }>(`${baseUrl(workspaceId)}/schedules${qs}`);
    return data.schedules;
  },

  async cancelSchedule(workspaceId: string, scheduleId: string): Promise<ContentSchedule> {
    return request<ContentSchedule>(`${baseUrl(workspaceId)}/schedules/${scheduleId}`, {
      method: 'PATCH',
      body: JSON.stringify({ status: 'cancelled' }),
    });
  },

  async publishItem(workspaceId: string, itemId: string, scheduledAt?: string): Promise<ContentSchedule> {
    return request<ContentSchedule>(`${baseUrl(workspaceId)}/distribution/publish`, {
      method: 'POST',
      body: JSON.stringify({ content_item_id: itemId, scheduled_at: scheduledAt }),
    });
  },

  async distributeSocial(
    workspaceId: string,
    itemId: string,
    authorIds: string[],
    platforms?: string[],
    scheduledAt?: string
  ): Promise<ContentSchedule> {
    return request<ContentSchedule>(`${baseUrl(workspaceId)}/distribution/social`, {
      method: 'POST',
      body: JSON.stringify({
        content_item_id: itemId,
        author_ids: authorIds,
        platforms,
        scheduled_at: scheduledAt,
      }),
    });
  },

  // Comments
  async getComments(workspaceId: string, itemId: string): Promise<{ comments: ContentComment[]; count: number }> {
    return request<{ comments: ContentComment[]; count: number }>(`${baseUrl(workspaceId)}/items/${itemId}/comments`);
  },

  async syncComments(workspaceId: string, itemId: string): Promise<{ status: string; message: string; synced: number }> {
    return request<{ status: string; message: string; synced: number }>(`${baseUrl(workspaceId)}/items/${itemId}/comments/sync`, {
      method: 'POST',
    });
  },

  async replyToComment(workspaceId: string, itemId: string, commentId: string, replyText: string): Promise<{ comment: ContentComment; message: string }> {
    return request<{ comment: ContentComment; message: string }>(`${baseUrl(workspaceId)}/items/${itemId}/comments/${commentId}/reply`, {
      method: 'POST',
      body: JSON.stringify({ reply_text: replyText }),
    });
  },

  // Per-Item Analytics
  async getItemAnalytics(workspaceId: string, itemId: string, days?: number): Promise<ItemAnalyticsDataPoint[]> {
    const qs = days ? `?days=${days}` : '';
    const data = await request<{ data: ItemAnalyticsDataPoint[] }>(`${baseUrl(workspaceId)}/analytics/${itemId}${qs}`);
    return data.data;
  },

  // Channel Credentials
  async getCredentials(workspaceId: string, authorId: string): Promise<ChannelCredential[]> {
    const data = await request<{ credentials: ChannelCredential[] }>(
      `${baseUrl(workspaceId)}/channels/${authorId}/credentials`
    );
    return data.credentials;
  },

  async storeCredentials(
    workspaceId: string,
    authorId: string,
    credentials: { li_at: string; jsessionid: string; platform?: string }
  ): Promise<{ credential: ChannelCredential; validation_error: string | null }> {
    return request<{ credential: ChannelCredential; validation_error: string | null }>(
      `${baseUrl(workspaceId)}/channels/${authorId}/credentials`,
      { method: 'POST', body: JSON.stringify(credentials) }
    );
  },

  async deleteCredentials(workspaceId: string, authorId: string, platform: string): Promise<void> {
    await request<void>(
      `${baseUrl(workspaceId)}/channels/${authorId}/credentials/${platform}`,
      { method: 'DELETE' }
    );
  },

  // All schedules (including completed/failed)
  async getAllSchedules(workspaceId: string): Promise<ContentSchedule[]> {
    const data = await request<{ schedules: ContentSchedule[] }>(`${baseUrl(workspaceId)}/schedules`);
    return data.schedules;
  },

  // AI Generation
  async generateSocialCopy(
    workspaceId: string,
    data: GenerateSocialCopyRequest
  ): Promise<GenerateSocialCopyResponse> {
    return request<GenerateSocialCopyResponse>(
      `${baseUrl(workspaceId)}/ai/generate-social`,
      { method: 'POST', body: JSON.stringify(data) }
    );
  },

  async generateSocialCopyBatch(
    workspaceId: string,
    data: BatchGenerateRequest
  ): Promise<BatchGenerateResponse> {
    return request<BatchGenerateResponse>(
      `${baseUrl(workspaceId)}/ai/generate-social/batch`,
      { method: 'POST', body: JSON.stringify(data) }
    );
  },

  // Style Analysis
  async analyzeStyle(workspaceId: string, authorId: string): Promise<StyleProfile> {
    return request<StyleProfile>(
      `${baseUrl(workspaceId)}/ai/analyze-style`,
      { method: 'POST', body: JSON.stringify({ author_id: authorId }) }
    );
  },

  // Essay Sectioning
  async sectionEssay(workspaceId: string, essayText: string, numSections?: number): Promise<{ sections: EssaySection[] }> {
    return request<{ sections: EssaySection[] }>(
      `${baseUrl(workspaceId)}/ai/section-essay`,
      { method: 'POST', body: JSON.stringify({ essay_text: essayText, num_sections: numSections }) }
    );
  },

  // Series Generation
  async generateSeries(
    workspaceId: string,
    params: {
      source_url: string;
      author_id: string;
      project_id: string;
      campaign?: string;
      num_sections?: number;
      analyze_style?: boolean;
    }
  ): Promise<SeriesResult> {
    return request<SeriesResult>(
      `${baseUrl(workspaceId)}/ai/generate-series`,
      { method: 'POST', body: JSON.stringify(params) }
    );
  },

  // Brand Voice
  async getBrandVoice(workspaceId: string): Promise<BrandVoice | null> {
    const data = await request<{ brand_voice: BrandVoice | null }>(
      `${baseUrl(workspaceId)}/brand-voice`
    );
    return data.brand_voice;
  },

  async upsertBrandVoice(
    workspaceId: string,
    data: Partial<BrandVoice>
  ): Promise<BrandVoice> {
    return request<BrandVoice>(
      `${baseUrl(workspaceId)}/brand-voice`,
      { method: 'PUT', body: JSON.stringify(data) }
    );
  },

  // ── Media Assets ────────────────────────────────────────────────────────────

  async listMedia(
    workspaceId: string,
    params?: { search?: string; mime_type?: string; tags?: string; limit?: number; offset?: number }
  ): Promise<{ assets: MediaAsset[]; total: number; limit: number; offset: number }> {
    const qs = new URLSearchParams();
    if (params?.search) qs.set('search', params.search);
    if (params?.mime_type) qs.set('mime_type', params.mime_type);
    if (params?.tags) qs.set('tags', params.tags);
    if (params?.limit != null) qs.set('limit', String(params.limit));
    if (params?.offset != null) qs.set('offset', String(params.offset));
    const q = qs.toString() ? `?${qs.toString()}` : '';
    return request<{ assets: MediaAsset[]; total: number; limit: number; offset: number }>(
      `${baseUrl(workspaceId)}/media${q}`
    );
  },

  async uploadMedia(workspaceId: string, file: File): Promise<MediaAsset> {
    const fd = new FormData();
    fd.append('file', file);
    const response = await fetchWithRetry(`${baseUrl(workspaceId)}/media/upload`, {
      method: 'POST',
      body: fd,
      credentials: 'include',
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new ApiError(response.status, errorText || response.statusText);
    }
    return response.json();
  },

  async updateMedia(workspaceId: string, id: string, data: { alt_text?: string; caption?: string; tags?: string[] }): Promise<MediaAsset> {
    return request<MediaAsset>(`${baseUrl(workspaceId)}/media/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  async deleteMedia(workspaceId: string, id: string): Promise<void> {
    await request<void>(`${baseUrl(workspaceId)}/media/${id}`, { method: 'DELETE' });
  },

  // ── Version History ─────────────────────────────────────────────────────────

  async listVersions(
    workspaceId: string,
    itemId: string,
    params?: { limit?: number; offset?: number }
  ): Promise<{ versions: ContentVersion[]; total: number }> {
    const qs = new URLSearchParams();
    if (params?.limit != null) qs.set('limit', String(params.limit));
    if (params?.offset != null) qs.set('offset', String(params.offset));
    const q = qs.toString() ? `?${qs.toString()}` : '';
    return request<{ versions: ContentVersion[]; total: number }>(
      `${baseUrl(workspaceId)}/items/${itemId}/versions${q}`
    );
  },

  async getVersion(workspaceId: string, itemId: string, versionId: string): Promise<ContentVersion> {
    return request<ContentVersion>(`${baseUrl(workspaceId)}/items/${itemId}/versions/${versionId}`);
  },

  async restoreVersion(workspaceId: string, itemId: string, versionId: string): Promise<{ success: boolean; item: ContentItem }> {
    return request(`${baseUrl(workspaceId)}/items/${itemId}/versions/${versionId}/restore`, { method: 'POST' });
  },
};
